import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  Divider,
} from '@mui/material';
import { PopupDialog, OrigenChip, LookupRadio, LookupListItem } from '../../../../components/common';
import { type DERCFiltered } from '../types/derc';
import { useDERCStore } from '../store';

interface DercSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (derc: DERCFiltered) => void;
  title?: string;
}

const DercSelectionModal: React.FC<DercSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  title = 'Seleccionar DERC'
}) => {
  const [selectedDerc, setSelectedDerc] = useState<DERCFiltered | null>(null);

  const {
    filteredDERCs,
    loading,
    error,
    fetchFilteredDERCs,
    clearError
  } = useDERCStore();

  useEffect(() => {
    if (open) {
      fetchFilteredDERCs();
      setSelectedDerc(null);
    }
  }, [open, fetchFilteredDERCs]);

  const handleSelect = () => {
    if (selectedDerc) {
      onSelect(selectedDerc);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedDerc(null);
    onClose();
  };

  const handleDercClick = (derc: DERCFiltered) => {
    setSelectedDerc(derc);
  };

  const handleCloseError = () => {
    clearError();
  };

  return (
    <PopupDialog
      open={open}
      onClose={handleClose}
      title={title}
      maxWidth="md"
      primaryAction={{
        label: 'Seleccionar',
        onClick: handleSelect,
        disabled: !selectedDerc || loading,
        loading: loading,
        variant: 'contained',
        color: 'primary'
      }}
      secondaryAction={{
        label: 'Cancelar',
        onClick: handleClose,
        variant: 'outlined',
        color: 'inherit'
      }}
    >
      {error && (
        <Alert
          severity="error"
          onClose={handleCloseError}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ minHeight: 300, maxHeight: 400, overflow: 'auto' }}>
          {filteredDERCs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              No hay DERCs disponibles
            </Typography>
          ) : (
            <List>
              {filteredDERCs.map((derc, index) => (
                <React.Fragment key={derc.idDocumento}>
                  <ListItem disablePadding >
                    <ListItemButton
                      sx={{ p: 0 }}
                      onClick={() => handleDercClick(derc)}
                      selected={selectedDerc?.idDocumento === derc.idDocumento}
                    >
                      <LookupRadio
                        checked={selectedDerc?.idDocumento === derc.idDocumento}
                        value={derc.idDocumento}
                      />
                      <OrigenChip
                        idOrigenProducto={derc.idOrigenProducto}
                        size="small"                        
                      />
                      <LookupListItem
                        mainText={`DERC ${derc.idDocumento}`}
                        secondaryText={`${derc.fechaExtraccion ? new Date(derc.fechaExtraccion).toLocaleDateString('es-ES') : 'N/A'} - ${derc.numeroMallas} mallas - ${derc.asociacionDescripcion ? (derc.asociacionDescripcion.length > 15 ? `${derc.asociacionDescripcion.substring(0, 15)}...` : derc.asociacionDescripcion) : 'N/A'}`}
                        sx={{ justifyContent: 'space-between', ml: 2 }}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredDERCs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      )}
    </PopupDialog>
  );
};

export default DercSelectionModal;